import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FuturesConfig.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * FuturesConfig - Configuración del bot de Futures
 */
export default function FuturesConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    capitalUsagePercent: 0.30,
    defaultLeverage: 5,
    allowedSymbols: ['BTCUSDT', 'ETHUSDT'],
    autoStopLossEnabled: true,
    autoStopLossPct: 0.02,
    autoTakeProfitEnabled: false,
    autoTakeProfitPct: 0.03
  });
  
  useEffect(() => {
    fetchConfig();
  }, []);
  
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('app_token');
      const res = await axios.get(`${API_BASE_URL}/bot/config/futures`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      });
      
      if (res.data.success && res.data.data) {
        setConfig(res.data.data);
        setFormData({
          capitalUsagePercent: res.data.data.capitalUsagePercent,
          defaultLeverage: res.data.data.defaultLeverage,
          allowedSymbols: res.data.data.allowedSymbols,
          autoStopLossEnabled: res.data.data.autoStopLossEnabled,
          autoStopLossPct: res.data.data.autoStopLossPct,
          autoTakeProfitEnabled: res.data.data.autoTakeProfitEnabled,
          autoTakeProfitPct: res.data.data.autoTakeProfitPct
        });
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      setMessage({ type: 'error', text: 'Config not found. Using defaults.' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSymbolToggle = (symbol) => {
    setFormData(prev => {
      const symbols = [...prev.allowedSymbols];
      const index = symbols.indexOf(symbol);
      
      if (index > -1) {
        symbols.splice(index, 1);
      } else {
        symbols.push(symbol);
      }
      
      return { ...prev, allowedSymbols: symbols };
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('app_token');
      const res = await axios.post(`${API_BASE_URL}/bot/config/futures`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      });
      
      if (res.data.success) {
        setConfig(res.data.data);
        setMessage({ type: 'success', text: 'Configuration saved successfully!' });
        
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Error saving configuration' 
      });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <div className="futures-config loading">Loading configuration...</div>;
  }
  
  return (
    <div className="futures-config">
      <div className="config-header">
        <h1>Futures Bot Configuration</h1>
        <p className="config-subtitle">
          Configure how the Futures bot will execute TradingView signals for you
        </p>
      </div>
      
      {message && (
        <div className={`message message--${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="config-info-box">
        <h3>⚡ How it Works</h3>
        <p>
          This bot receives signals from TradingView via webhook (MASTER mode).
          When a signal arrives, it automatically executes for ALL users with the bot enabled.
        </p>
        <p>
          <strong>You configure:</strong> Capital usage, leverage, allowed symbols
        </p>
        <p>
          <strong>Admin configures:</strong> TradingView strategy and webhook
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="config-form">
        {/* Capital Usage */}
        <div className="form-section">
          <h3>💰 Capital Management</h3>
          
          <div className="form-group">
            <label>
              Capital Usage per Trade
              <span className="label-help">Percentage of balance to use per trade</span>
            </label>
            <div className="input-with-addon">
              <input
                type="number"
                min="0.01"
                max="1"
                step="0.01"
                value={formData.capitalUsagePercent}
                onChange={(e) => handleInputChange('capitalUsagePercent', parseFloat(e.target.value))}
              />
              <span className="addon">{(formData.capitalUsagePercent * 100).toFixed(0)}%</span>
            </div>
            <p className="help-text">
              Example: With 1000 USDT and 30%, you'll use 300 USDT per trade
            </p>
          </div>
          
          <div className="form-group">
            <label>
              Default Leverage
              <span className="label-help">Multiplier for your positions</span>
            </label>
            <div className="input-with-addon">
              <input
                type="number"
                min="1"
                max="20"
                step="1"
                value={formData.defaultLeverage}
                onChange={(e) => handleInputChange('defaultLeverage', parseInt(e.target.value))}
              />
              <span className="addon">{formData.defaultLeverage}x</span>
            </div>
            <p className="help-text warning">
              ⚠️ Higher leverage = Higher risk. Trade responsibly.
            </p>
          </div>
        </div>
        
        {/* Allowed Symbols */}
        <div className="form-section">
          <h3>📊 Allowed Symbols</h3>
          <p className="section-description">
            Select which symbols the bot can trade when signals arrive
          </p>
          
          <div className="symbol-grid">
            {['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'ADAUSDT'].map(symbol => (
              <label key={symbol} className="symbol-checkbox">
                <input
                  type="checkbox"
                  checked={formData.allowedSymbols.includes(symbol)}
                  onChange={() => handleSymbolToggle(symbol)}
                />
                <span>{symbol}</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Risk Management */}
        <div className="form-section">
          <h3>🛡️ Risk Management</h3>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.autoStopLossEnabled}
                onChange={(e) => handleInputChange('autoStopLossEnabled', e.target.checked)}
              />
              <span>Enable Auto Stop Loss</span>
            </label>
            
            {formData.autoStopLossEnabled && (
              <div className="nested-input">
                <label>Stop Loss Percentage</label>
                <div className="input-with-addon">
                  <input
                    type="number"
                    min="0.01"
                    max="0.5"
                    step="0.01"
                    value={formData.autoStopLossPct}
                    onChange={(e) => handleInputChange('autoStopLossPct', parseFloat(e.target.value))}
                  />
                  <span className="addon">{(formData.autoStopLossPct * 100).toFixed(0)}%</span>
                </div>
                <p className="help-text">
                  With 5x leverage, 2% price move = 10% loss on margin
                </p>
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.autoTakeProfitEnabled}
                onChange={(e) => handleInputChange('autoTakeProfitEnabled', e.target.checked)}
              />
              <span>Enable Auto Take Profit</span>
            </label>
            
            {formData.autoTakeProfitEnabled && (
              <div className="nested-input">
                <label>Take Profit Percentage</label>
                <div className="input-with-addon">
                  <input
                    type="number"
                    min="0.01"
                    max="0.5"
                    step="0.01"
                    value={formData.autoTakeProfitPct}
                    onChange={(e) => handleInputChange('autoTakeProfitPct', parseFloat(e.target.value))}
                  />
                  <span className="addon">{(formData.autoTakeProfitPct * 100).toFixed(0)}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Submit */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          
          <button 
            type="button" 
            className="btn-cancel"
            onClick={fetchConfig}
          >
            Reset
          </button>
        </div>
      </form>
      
      {/* Info Box */}
      <div className="webhook-info-box">
        <h3>🔗 Webhook Information</h3>
        <p>
          <strong>Webhook URL:</strong> <code>{window.location.origin}/api/webhook</code>
        </p>
        <p>
          <strong>Secret:</strong> Configured globally by admin (TRADINGVIEW_SECRET in .env)
        </p>
        <p className="note">
          Configure this webhook URL in your TradingView alerts. 
          The bot will automatically execute trades based on your configuration above.
        </p>
      </div>
    </div>
  );
}
